/**
 * setStory — one sentence about a set, the highest TRUE rung of a fact ladder
 * (the recap-council "Delight Ladder" pattern, applied web-side).
 *
 * v1 is deterministic and web-deployed. The Swift fast-follow generates a phrased
 * sentence per set at firehose-save time (FoundationModels @Generable) and rides
 * it on the payload; this module PREFERS that aiStory when present and falls back
 * to the template otherwise — same slot, capability-gated by nature (phones
 * without Apple Intelligence simply never produce the field).
 *
 * Rules: one sentence, <=12 words, NO digits (numbers live in the meta row). The
 * provenance ✦ is a UI concern — this module returns plain text; the component
 * prefixes the star only when it passed a non-empty aiStory (so the component
 * already knows the provenance without parsing the string).
 *
 * VARIANT POOLS (de-monotony): a swim routinely has several sets on the SAME rung
 * (e.g. four positive-split sets on June 11). Returning one canned sentence makes
 * the screen read like a bug. Each rung carries a small pool of honest restatements
 * of the SAME fact; selection is deterministic and collision-avoiding WITHIN a
 * swim — the variant index is THIS set's ordinal among the sets that fired the
 * same rung, modulo the pool size. No randomness, no Date.now(); identical input
 * always yields identical output.
 *
 * Pure module.
 */
import type { SetGroup } from "./setGroups";
import { includedLaps } from "./setMath";

/** Even-pace threshold: lap-duration coefficient of variation below this. */
const EVEN_CV = 0.06;
/** Split sensitivity: half-averages must differ by more than this fraction. */
const SPLIT_DELTA = 0.03;

/**
 * Rung identifiers. Each maps to a pool of honest, interchangeable sentences —
 * every variant in a pool must be a TRUE restatement of the same underlying fact,
 * <=12 words, digit-free, plain English. The fallback rung is data-shaped (it
 * spells the stroke), so it is generated rather than pooled.
 */
type Rung =
  | "fastest"
  | "negative"
  | "even"
  | "positive"
  | "varied"
  | "mixedStroke"
  | "fallback";

/**
 * Sentence pools per pooled rung. Order is the deterministic variant order:
 * the first same-rung set in a swim gets index 0, the next index 1, and so on
 * (wrapping at the pool length). Keep each <=12 words and digit-free — the
 * setStory tests enforce both for every pool entry.
 */
const POOLS: Record<Exclude<Rung, "fallback">, string[]> = {
  fastest: [
    "Your fastest set of the swim.",
    "The quickest set you swam today.",
    "Nothing else this swim was faster.",
    "Your speediest stretch of the whole swim.",
  ],
  negative: [
    "You sped up on the back half.",
    "Faster coming home than going out.",
    "You closed harder than you opened.",
    "The back half was your quicker half.",
  ],
  even: [
    "You held your pace the whole way.",
    "Steady from the first length to the last.",
    "Same pace start to finish.",
    "You kept it level the entire set.",
  ],
  positive: [
    "You went out fast and cruised home.",
    "Strong start, easy finish.",
    "All the speed up front.",
    "Fast early lengths, relaxed late ones.",
  ],
  // Balanced halves but lengths that swing (no clean fade or build). Says only
  // what is true and digit-free: the pace moved around. Without this, such a set
  // fell through to a bare "even" and falsely claimed it held pace over a big
  // spread (the B1 bug). A pool, not one line, so a swim with several such sets
  // doesn't repeat a sentence and read like a render bug.
  varied: [
    "Your pace bounced around this set.",
    "Up and down, length to length.",
    "No rhythm to settle into here.",
    "Your lengths came in uneven.",
  ],
  // Mixed-stroke sets make NO pace-shape claim (durations differ by stroke, not
  // by effort). These say only what is true and digit-free: several strokes in
  // one set. A pool (not one canned line) so a swim with several mixed sets
  // doesn't render the same sentence twice and read like a bug.
  mixedStroke: [
    "A mix of strokes in one set.",
    "Several strokes back to back.",
    "More than one stroke this set.",
    "A set that switched strokes.",
  ],
};

/**
 * FIRST-PERSON mirror of POOLS, used only on the PUBLIC showcase page (the owner
 * picked "my swim / my pace"). Same rung keys, same pool LENGTHS and order as
 * POOLS, so the variant-index machinery and the no-duplicate-sentence property
 * carry over verbatim — the renderer just reads from this table when the
 * firstPerson flag is set. Every entry is the same TRUE claim as its POOLS twin,
 * <=12 words, digit-free, with zero second-person you/your. The mixedStroke pool
 * is already voice-neutral, so it is reused as-is (no first-person variant
 * needed). The setStory tests enforce <=12 words + digit-free on these too.
 */
const POOLS_FP: Record<Exclude<Rung, "fallback" | "mixedStroke">, string[]> = {
  fastest: [
    "My fastest set of the swim.",
    "The quickest set I swam today.",
    "Nothing else this swim was faster.",
    "My speediest stretch of the whole swim.",
  ],
  negative: [
    "I sped up on the back half.",
    "Faster coming home than going out.",
    "I closed harder than I opened.",
    "The back half was my quicker half.",
  ],
  even: [
    "I held my pace the whole way.",
    "Steady from the first length to the last.",
    "Same pace start to finish.",
    "I kept it level the entire set.",
  ],
  positive: [
    "I went out fast and cruised home.",
    "Strong start, easy finish.",
    "All the speed up front.",
    "Fast early lengths, relaxed late ones.",
  ],
  varied: [
    "My pace bounced around this set.",
    "Up and down, length to length.",
    "No rhythm to settle into here.",
    "My lengths came in uneven.",
  ],
};

/** Pick the right pool for a rung + voice. mixedStroke is voice-neutral and
 *  lives only in POOLS; every other pooled rung has a first-person mirror. */
function poolFor(rung: Exclude<Rung, "fallback">, firstPerson: boolean): string[] {
  if (firstPerson && rung !== "mixedStroke") return POOLS_FP[rung];
  return POOLS[rung];
}

/** Average seconds per INCLUDED length. Infinity when every lap is excluded —
 *  an all-excluded set can never win "fastest". */
function avgSecPerLength(set: SetGroup): number {
  const laps = includedLaps(set);
  if (laps.length === 0) return Infinity;
  const total = laps.reduce((s, l) => s + l.durationSeconds, 0);
  return total / laps.length;
}

/** Mean of a list. */
function mean(xs: number[]): number {
  return xs.length === 0 ? 0 : xs.reduce((s, x) => s + x, 0) / xs.length;
}

/** Coefficient of variation (population SD / mean) of INCLUDED lap durations. */
function durationCV(set: SetGroup): number {
  const xs = includedLaps(set).map((l) => l.durationSeconds);
  const m = mean(xs);
  if (m === 0) return 0;
  const variance = mean(xs.map((x) => (x - m) ** 2));
  return Math.sqrt(variance) / m;
}

/**
 * Split halves of the set by lap index. Odd count: the middle lap joins the
 * FIRST half (documented tie-break). Returns [firstHalfMeanDur, secondHalfMeanDur].
 */
function halfMeans(set: SetGroup): [number, number] {
  const durs = includedLaps(set).map((l) => l.durationSeconds);
  const splitAt = Math.ceil(durs.length / 2); // middle joins first half
  const first = durs.slice(0, splitAt);
  const second = durs.slice(splitAt);
  return [mean(first), mean(second)];
}

function isFastestSet(set: SetGroup, allSets: SetGroup[]): boolean {
  // Multi-lap = >= 2 INCLUDED laps; a set of phantoms is not a competitor.
  const multiLap = allSets.filter((s) => includedLaps(s).length >= 2);
  if (multiLap.length < 2 || includedLaps(set).length < 2) return false;
  const mine = avgSecPerLength(set);
  // Strictly fastest — no ties claiming "fastest".
  return multiLap.every((s) => s === set || avgSecPerLength(s) > mine);
}

/**
 * True when the set's INCLUDED laps span more than one stroke style. Lap
 * DURATION is not comparable across strokes (a 25s butterfly length and a 38s
 * breaststroke length are both "on pace" for their stroke), so the half-split
 * and coefficient-of-variation pace-SHAPE rungs (even / positive / negative)
 * would assert a consistency or fade the splits refute — e.g. a Killer BS set
 * reading "You held your pace the whole way" directly above a 14s fly-vs-breast
 * spread. Mixed-stroke sets therefore skip the pace-shape rungs entirely (this
 * fixes the APP, not just the showcase). The cross-set "fastest" superlative is
 * a different claim — quickest set of the swim, not within-set consistency — so
 * it stays available; mixed-stroke sets just route to the digit-free stroke
 * fallback ("A mix of strokes, front to back.") instead of a false pace shape.
 */
function isMultiStroke(set: SetGroup): boolean {
  const strokes = new Set(includedLaps(set).map((l) => l.strokeStyle));
  return strokes.size > 1;
}

/**
 * Walk the fact ladder and return the highest TRUE rung for this set. Pure —
 * depends only on the set and its swim-mates (for the "fastest" comparison).
 */
function rungFor(set: SetGroup, allSets: SetGroup[]): Rung {
  // Single-INCLUDED-length sets have no split / pace story — straight to the
  // fallback. (Excluded laps carry no pace claim; see setMath module doc.)
  if (includedLaps(set).length < 2) return "fallback";

  if (isFastestSet(set, allSets)) return "fastest";

  // Mixed-stroke sets: pace SHAPE is not comparable across strokes (see
  // isMultiStroke). Skip even/positive/negative; route to the mixed-stroke pool.
  if (isMultiStroke(set)) return "mixedStroke";

  const [firstHalf, secondHalf] = halfMeans(set);
  if (firstHalf > 0 && secondHalf > 0) {
    if (secondHalf < firstHalf * (1 - SPLIT_DELTA)) return "negative";
    if (durationCV(set) < EVEN_CV) return "even";
    if (firstHalf < secondHalf * (1 - SPLIT_DELTA)) return "positive";
    // Halves balance but the CV floor was cleared above → the lengths swung
    // without a clean fade or build. NOT "even" — claiming held-pace here is
    // false over a real spread (the B1 bug). Honest rung: the pace varied.
    return "varied";
  }
  return "fallback";
}

/**
 * Variant index for a pooled rung: this set's ORDINAL among the sets (in swim
 * order) that fired the same rung. Deterministic and collision-avoiding within
 * the swim — two same-rung sets only collide when the pool wraps (>pool-size
 * sets on one rung), and even then they cycle rather than repeat back-to-back.
 */
function variantIndex(set: SetGroup, allSets: SetGroup[], rung: Rung): number {
  let ordinal = 0;
  for (const s of allSets) {
    if (s === set) break;
    if (rungFor(s, allSets) === rung) ordinal += 1;
  }
  return ordinal;
}

export function storyFor(
  set: SetGroup,
  allSets: SetGroup[],
  aiStory?: string | null,
  firstPerson = false,
): string {
  // AI insertion point — prefer the phrased sentence when present.
  if (typeof aiStory === "string" && aiStory.trim().length > 0) {
    return aiStory;
  }

  // All members excluded → no honest pace/stroke claim exists. Return "" and
  // let the sheet hide the story line entirely.
  if (includedLaps(set).length === 0) return "";

  const rung = rungFor(set, allSets);
  if (rung === "fallback") return fallbackTruth(set);

  // firstPerson selects the first-person pool mirror (showcase only); the
  // variant index is computed identically so the no-duplicate property holds.
  const pool = poolFor(rung, firstPerson);
  const idx = variantIndex(set, allSets, rung) % pool.length;
  return pool[idx];
}

/** Selection rank per rung — the hero quotes the most interesting set, where
 *  interesting follows the same ladder the sentences do. PRIVATE, like rungFor. */
const RUNG_RANK: Record<Rung, number> = {
  fastest: 4,
  negative: 3,
  even: 2,
  positive: 1,
  // A swung-pace set is a weaker hero subject than any clean pace shape (no
  // tidy story) but more interesting than a single-stroke fallback line.
  varied: 0.7,
  // Above the bare fallback (a mix-of-strokes set is a more interesting
  // hero subject than a single-stroke fallback line) but below every pace
  // shape (a true fade/hold story wins the hero pull-quote). Mixed-stroke sets
  // can still win the hero via the "fastest" rung, which is computed separately.
  mixedStroke: 0.5,
  fallback: 0,
};

/**
 * Swim-level pull-quote for SwimHeroCard: the story sentence of the swim's
 * highest-rung set. SELECTION is by data rung (fastest > negative > even >
 * positive > fallback) over sets with at least one INCLUDED lap; ties break to
 * more included laps, then greater meters, then first in swim order (encoded
 * by replacing only on STRICT improvement while iterating in order). The
 * SENTENCE comes from storyFor(winner, sets, winner.aiStory), so aiStory
 * precedence and the variant pools are reused, never forked — with ONE
 * hero-only exception (VP gate FIX_FIRST): a fastest-rung winner without an
 * aiStory gets "{label} was your fastest stretch." instead of the pool line
 * ("my" when firstPerson is set for the public showcase page).
 * The fastest pool sentences are set-self-referential ("Your fastest set of
 * the swim.") and dangle at swim level with no named subject; every other
 * rung describes a pace shape and parses fine, so those pass through
 * untouched — as does the detail sheet, where the pool sentence is still
 * correct IN context. Hero and sheet phrasing the same claim differently is
 * sanctioned. Returns null when no set is eligible — the hero stays
 * numbers-only.
 */
export function heroStory(sets: SetGroup[], firstPerson = false): string | null {
  let winner: SetGroup | null = null;
  let winnerRank = -1;
  for (const s of sets) {
    const lapCount = includedLaps(s).length;
    if (lapCount === 0) continue; // all-excluded — no honest claim exists
    const rank = RUNG_RANK[rungFor(s, sets)];
    if (winner === null) {
      winner = s;
      winnerRank = rank;
      continue;
    }
    const winnerLaps = includedLaps(winner).length;
    if (
      rank > winnerRank ||
      (rank === winnerRank && lapCount > winnerLaps) ||
      (rank === winnerRank && lapCount === winnerLaps && s.meters > winner.meters)
    ) {
      winner = s;
      winnerRank = rank;
    }
  }
  if (winner === null) return null;

  // Hero-only swim-level line for the fastest rung — names the winning set.
  // The "{label} was your fastest stretch." template sidesteps both grammar
  // traps in the label inventory: "Mixed set" (no "set set" double) and
  // "The Rockalita" (no "Your The"). aiStory still wins verbatim (same
  // non-empty test storyFor applies). "The whole swim" is structurally
  // unreachable here (the fastest rung needs 2+ multi-lap sets; that label
  // only exists on single-set swims) — guarded anyway: the degenerate label
  // falls back to the normal storyFor sentence.
  //
  // Warmup/Cooldown are POSITION guesses (first set / last set), not verified
  // owner intent — the app cannot know whether a set was actually a warmup or
  // cooldown swim. Naming them directly ("Warmup was your fastest stretch.")
  // asserts intent the app doesn't have. Neutral position-fact phrasing
  // replaces the label for these two cases only; every other label still
  // names the set directly (steering-panel fix, 2026-07-05).
  const hasAiStory =
    typeof winner.aiStory === "string" && winner.aiStory.trim().length > 0;
  if (
    !hasAiStory &&
    winnerRank === RUNG_RANK.fastest &&
    winner.label !== "The whole swim"
  ) {
    if (winner.label === "Warmup") {
      return firstPerson
        ? "My first stretch was my fastest."
        : "Your first stretch was your fastest.";
    }
    if (winner.label === "Cooldown") {
      return firstPerson
        ? "My last stretch was my fastest."
        : "Your last stretch was your fastest.";
    }
    // firstPerson (showcase) → "my", else "your". Same template otherwise.
    return `${winner.label} was ${firstPerson ? "my" : "your"} fastest stretch.`;
  }

  const sentence = storyFor(winner, sets, winner.aiStory, firstPerson);
  // Defensive: eligible winners never produce "", but the signature says string.
  return sentence === "" ? null : sentence;
}

/** Digit-free plain-truth fallback ("A breaststroke length." style), over
 *  included laps (storyFor already returned "" when none are included). */
function fallbackTruth(set: SetGroup): string {
  const laps = includedLaps(set);
  const strokes = new Set(laps.map((l) => strokeWord(l.strokeStyle)));
  if (laps.length === 1) {
    return `A ${strokes.values().next().value} length.`;
  }
  if (strokes.size === 1) {
    return `Steady ${strokes.values().next().value}, front to back.`;
  }
  return "A mix of strokes, front to back.";
}

function strokeWord(stroke: string): string {
  // One voice: spell "freestyle" in full, matching how the rest of the app
  // names the stroke. The old "free" was the lone abbreviation in prose
  // (every other stroke was already spelled out), which read inconsistently.
  return stroke.toLowerCase();
}
